import { useState, useEffect } from 'react';
import api from '../../../utils/api';

export const useBookData = (selectedBookType) => {
  // Book data state
  const [book, setBook] = useState(null);
  const [sections, setSections] = useState([]);
  const [originalBook, setOriginalBook] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [search, setSearch] = useState('');
  const [filteredChapters, setFilteredChapters] = useState([]);
  const [openSections, setOpenSections] = useState({ 'no-section': true });

  // Fetch the original (public) book and its sections (no admin role required)
  useEffect(() => {
    const token = localStorage.getItem('app_token');
    if (!token) return;

    const headers = {
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    };

    api.get('/api/books/original', { headers })
      .then(bookRes => {
        let chapters = bookRes.data.chapters;
        if (typeof chapters === 'string') {
          try { chapters = JSON.parse(chapters); } catch { chapters = []; }
        }
        setBook({ ...bookRes.data, chapters: chapters || [] });
        setFilteredChapters(chapters || []);
        const bookId = bookRes.data.id;
        return api.get(`/api/books/${bookId}/sections`, { headers });
      })
      .then(sectionsRes => {
        setSections(sectionsRes.data || []);
      })
      .catch(err => {
        console.error('useBookData: Error initializing book data:', err);
        setBook(null);
        setSections([]);
      });
  }, []);

  // Filter chapters based on search
  useEffect(() => {
    if (!book) return;
    if (!search) {
      setFilteredChapters(book.chapters || []);
    } else {
      const s = search.toLowerCase();
      setFilteredChapters(
        (book.chapters || []).filter(
          ch => ch.title.toLowerCase().includes(s) || (ch.content && ch.content.toLowerCase().includes(s))
        )
      );
    }
    setSelectedIdx(0);
  }, [search, book]);

  // Open first section and 'Other Chapters' by default
  useEffect(() => {
    if (sections.length > 0) {
      setOpenSections(prev => {
        const initial = { ...prev };
        if (sections[0]?.id) initial[sections[0].id] = true;
        initial['no-section'] = true;
        return initial;
      });
    } else {
      // Initialize with just 'no-section' if no sections exist
      setOpenSections({ 'no-section': true });
    }
  }, [sections.length]);

  // Handle book type selection - simplified for original only
  const handleBookTypeSelect = async (bookType) => {
    setLoading(true);
    setError(null);
    
    if (bookType === 'original') {
      // For original book, use the main book data
      setOriginalBook(book);
    }
    
    setLoading(false);
  };

  return {
    book,
    sections,
    originalBook,
    loading,
    error,
    selectedIdx,
    setSelectedIdx,
    search,
    setSearch,
    filteredChapters,
    openSections,
    setOpenSections,
    handleBookTypeSelect
  };
}; 